import React from 'react';
import { StaticPage, TopbarContainer } from '../../containers';
import {
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  NamedLink,
} from '../../components';
import { Helmet } from 'react-helmet-async';
import css from './WhatILearnedFromLoyaltyTestPage.module.css';

// Using images from public directory
const mainImage = '/static/images/blog/loyalty-test-ex-husband.jpg';
const menCheatImage = '/static/images/blog/why-do-men-cheat.jpg';
const testBoyfriendImage = '/static/images/blog/test-boyfriend.jpg';
const loyaltyTestImage = '/static/images/blog/loyalty-test.jpg';
const ariProfileImage = '/static/images/blog/Ari_loyalty_tester.png';
const siteImage = '/static/images/blog/Picture_of_site.png';
const text1Image = '/static/images/blog/text1.png';
const text2Image = '/static/images/blog/text2.png';

// Tester profile link
const testerProfileLink = '/l/ari-loyaltytest/64fb6375-e423-49be-bd4c-683270492285';

// NYT article link
const nytArticleLink = 'https://www.nytimes.com/2023/05/02/style/loyalty-tests-relationships.html';

// Social media links
const instagramLink = 'https://www.instagram.com/loyaltytest.official/';
const facebookLink = 'https://www.facebook.com/loyaltytestofficial/';

const WhatILearnedFromLoyaltyTestPage = () => {
  // FAQ data with schema markup
  const faqData = [
    {
      question: "What is a loyalty test?",
      answer: "A loyalty test is a service where a professional 'tester' contacts your partner through social media to see if they respond to flirtatious messages. The will tester will keep you updated every step of the way as the test is performed."
    },
    {
      question: "How much does a loyalty test cost?",
      answer: "Loyalty test prices vary from tester to tester. Basic social media tests can be as low as $10, and on the upper end, there are tests ranging up to $300."
    },
    {
      question: "Are loyalty tests ethical?",
      answer: "Loyalty tests as an ethical topic is quite a hot debate. Some people say they are an invasion of personal trust, however some believe its the only way to know your partner's loyalty for sure. It's important to have good communication and weigh your options before starting a loyalty test."
    },
    {
      question: "What happens if my partner passes the loyalty test?",
      answer: "If your partner passes the loyalty test by not engaging or blocking the tester, congrats your significant other is loyal! This can definitely provide you peace of mind and strengthen trust in your relationship. Its up to you whether you want to disclose it to them or not!"
    },
    {
      question: "What happens if my partner fails the loyalty test?",
      answer: "If your partner, arranges to meet up with the tester, or has some mutual flirting going on, the tester will immediately send proof to you. This can be hard to stomach at first but it will bring clarity to your relationship. It will allow you to make an informed decision about your future together."
    }
  ];

  // Related articles
  const relatedArticles = [
    {
      id: 1,
      title: 'Why Do Men Cheat?',
      description: 'Understand the psychology behind infidelity and what drives men to cheat in relationships.',
      image: menCheatImage,
      link: '/WhyDoMenCheat'
    },
    {
      id: 2,
      title: 'How to Test Your Boyfriend (Without Hurting Your Relationship)',
      description: 'Learn to recognize the subtle and obvious signs that could indicate infidelity in your relationship.',
      image: testBoyfriendImage,
      link: '/how-to-test-your-boyfriend'
    },
    {
      id: 3,
      title: 'Loyalty Test Website',
      description: 'A Loyalty Test Website: Your Most Pressing Questions Answered',
      image: loyaltyTestImage,
      link: '/loyalty-test-website'
    }
  ];

  // Generate blog schema
  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: 'What I Learned from a Loyalty Test on My (Ex)Husband',
    description: 'Is your partner staying faithful? Learn how I set up a loyalty test to catch a cheating spouse and gain closure.',
    image: mainImage,
    datePublished: new Date().toISOString(),
    dateModified: new Date().toISOString(),
    author: {
      '@type': 'Person',
      name: 'Anonymous'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Loyalty Test',
      logo: {
        '@type': 'ImageObject',
        url: 'https://loyalty-test.com/static/images/logo.png'
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://loyalty-test.com/what-i-learned-from-loyalty-test'
    }
  };

  return (
    <StaticPage
      title="What I Learned from a Loyalty Test on My (Ex)Husband"
      description="Is your partner staying faithful? Learn how I set up a loyalty test to catch a cheating spouse and gain closure."
      schema={blogSchema}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>
        
        <LayoutWrapperMain>
          <div className={css.staticPageWrapper}>
            {/* Additional SEO metadata */}
            <Helmet>
              <meta name="keywords" content="loyalty test, cheating spouse, relationship trust, infidelity, marriage problems" />
              <link rel="canonical" href="https://loyalty-test.com/what-i-learned-from-loyalty-test" />
              <meta property="og:type" content="article" />
              <meta property="og:title" content="What I Learned from a Loyalty Test on My (Ex)Husband" />
              <meta property="og:description" content="Is your partner staying faithful? Learn how I set up a loyalty test to catch a cheating spouse and gain closure." />
              <meta property="og:image" content={mainImage} />
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:title" content="What I Learned from a Loyalty Test on My (Ex)Husband" />
              <meta name="twitter:description" content="Is your partner staying faithful? Learn how I set up a loyalty test to catch a cheating spouse and gain closure." />
              <meta name="twitter:image" content={mainImage} />
              
              {/* Preload critical images */}
              <link rel="preload" href={ariProfileImage} as="image" />
              
              {/* Add image optimization hints */}
              <meta name="image-rendering" content="optimizeQuality" />
              <meta http-equiv="Accept-CH" content="DPR, Width, Viewport-Width" />
            </Helmet>

            <h1 className={css.pageTitle}>What I Learned from a Loyalty Test on My (Ex)Husband</h1>
            
            <div className={css.contentWrapper}>
              <div className={css.authorIntro}>
                <p><em>Hi all! I am a 35-year-old New Yorker. I do a lot of writing and I just wanted to share my experience with a loyalty test.</em></p>
              </div>
              
              <p>Would he or wouldn't he? Should I or shouldn't I? These were the questions running through my mind when I stumbled upon a review of Loyalty-Test.com in the <a href={nytArticleLink} target="_blank" rel="noopener noreferrer" className={css.articleLink}>New York Times</a>. I read through the article and found myself nodding along. My jaw dropped open. Women and men were paying 'testers' to, well, test their partners' loyalty (lol). The testers reach out to their prey through social media, sometimes even setting up in-person meetings, then take screenshots or photos as evidence. Voila. Busted.</p>
              
              {/* Site image with lazy loading */}
              <figure className={css.imageContainer}>
                <img 
                  src={siteImage} 
                  alt="Loyalty Test website homepage showing testers and services" 
                  className={css.gridImage} 
                  loading="lazy"
                  decoding="async"
                  fetchpriority="low"
                />
                <figcaption className={css.imageCaption}>The Loyalty-Test.com website featured in the New York Times article</figcaption>
              </figure>
              
              <p>Did I really think my husband would cheat, or even flirt? And if so, did I want to trap him? Part of me felt this was too dishonest, but the rest of me wanted to know. If I did this, there was no going back. Was I really someone who'd set up a trap to catch a cheating partner? My husband, let's call him Jim, had been acting off, weird, lately. At night, as we binged our latest obsession, I'd catch him on his phone. He always appeared to be texting, but when I asked, he'd claim he was simply playing a game. A game? This was new. Add in all the times I'd walked into his home office, and he'd hurriedly minimized his computer screen and you had a seriously suspicious situation.</p>
              
              <p>After deliberating back and forth for days, arguing the ethics with myself, trying to rationalize all the screen minimizations, all the furious clandestine texting (or gaming, whatever), reader, I did it. I signed up for a loyalty test.</p>
              
              <h2>Setting the Trap: How it's Done</h2>
              <p>Once I decided to pull the trigger, the process was pretty simple. I signed up, then I was able to actually choose the tester I wanted to flirt with my husband. This made me feel like the process would be, I don't know, realistic? Let's say I didn't get to choose, and they sent someone who wasn't his type to flirt with him, and he had no response, it wouldn't prove anything. This way, well, let's just say I know my husband's type.</p>
              
              <p>Once I chose her, I was prompted to choose a package. I chose social media messaging, because this seemed simple and straightforward. Either he failed or he passed and I'd have the screenshots to prove it. After I chose a package, I communicated with the tester, providing her with his social media handles. You could also provide her with details such as how you'd like her to approach your significant other, but to be honest, I felt flirting would be simple enough to give me an answer. No notes from me.</p>
              
              <p>Of course, I talk a big game, but I wasn't prepared for what happened next.</p>
              
              <h2>The Results: Did He Pass the Loyalty Test?</h2>
              <p>Unfortunately, my husband failed the test and it took no time at all. My tester sent my husband a DM on Instagram to see if he would respond. Her first message was simple. As soon as she told me her plan, I knew he'd respond. He's a dog guy, and very proud of our pups. So proud, all of his social media profile photos are with them, not me. So all she wrote was "heyyy cutie, your dog is adorable!" and he replied within hours, as I knew he would. Unfortunately, his response ended with heart emojis. Not great. At first, though, their conversation was innocent—aside from those heart emojis. They messaged back and forth about dogs, but then he asked for her Snapchat. Snapchat? I didn't even know my husband had a Snapchat account! Talk about unearthing secrets. After the Snapchat convo, he proposed meeting up- for a lot more then talking if I may add (dont really want to get into the details).</p>
              
              {/* Text message screenshots with lazy loading */}
              <div className={css.imageGrid}>
                <figure className={css.imageContainer}>
                  <img 
                    src={text1Image} 
                    alt="Screenshot of loyalty test text messages showing flirtatious conversation" 
                    className={css.gridImage} 
                    loading="lazy"
                    decoding="async"
                    fetchpriority="low"
                  />
                  <figcaption className={css.imageCaption}>Initial messages from the loyalty test</figcaption>
                </figure>
                <figure className={css.imageContainer}>
                  <img 
                    src={text2Image} 
                    alt="Screenshot of loyalty test text messages showing plans to meet up" 
                    className={css.gridImage} 
                    loading="lazy"
                    decoding="async"
                    fetchpriority="low"
                  />
                  <figcaption className={css.imageCaption}>Messages showing my husband arranging to meet</figcaption>
                </figure>
              </div>
              
              <p>But that was it. The end of the test. She was able to screenshot, block him, and report back to me. He'd failed. Had I won? I don't know. All I knew was I had to confront him and confront the very possible end of my marriage.</p>
              
              <h2>Prepare for the Emotional Toll</h2>
              <p>I had to really steel myself for the emotional toll this was about to take on me. Before I confronted my husband, I spoke to a close girlfriend. She pointed out there had been no actual infidelity, just an emotional affair. She asked me if I felt this was in the same ballpark as 'actual' cheating. Wasn't it, though? Either way, he'd considered it. He'd not only chatted with this woman but asked her to meet. I'm pretty sure he wasn't planning to advise her on the best dog breeds for her lifestyle (although, to be fair, he would, if asked).</p>
              
              <p>Finally, though, I sat him down and showed him what I'd done, and what I knew he'd done. Of course he was angry, defensive, but someone once told me you never have to be defensive if you're in the right. And he was very defensive. He felt I'd 'spied' on him, as if I was the one in the wrong here, but I'd expected this response. And while, yes, I did feel guilty, like I said at the beginning, I struggled with even clicking 'select', I still felt the ends justified the means.</p>
              
              <p>There was shouting, there were tears, there was bargaining, promises, but in the end, I decided I couldn't trust him anymore, and really, could he trust me? Would he always be wondering if the DM he'd just received was from his wife's spy? Or would he simply set up a second profile, one I didn't know about? See? I was already assuming the worst of him. No good could come of us staying together. My mind wouldn't let me get past this.</p>
              
              <p>We're now in the process of divorcing. Luckily, there are no kids, just dogs to fight for custody over (joint, thank you). They were actually the hardest part of all this. While I am in no way happy this loyalty test proved what I feared, it did provide me with closure. I no longer had to wonder what he was doing on his phone. No longer had to worry what was on those minimized screens.</p>
              
              <h2>The Ethics of Loyalty Tests: Right or Wrong?</h2>
              <p>There are moments I still feel horrible. I ask myself if this was really me, wonder how we got here. Lying to someone never feels good, and when it's your partner, well, you feel lower than low, even though I knew it was the only way to find out.</p>
              
              <p>I'm sure some of you will believe setting a trap isn't fair, you'll argue it's an invasion of privacy. And if you feel that way, don't do it. I weighed the pros and cons, the knowing versus not knowing, and came down on the side of knowing.</p>
              
              <p>Someone asked me what I would have done if he'd passed. Would I have told him I'd tested him? The answer? Hell no! He never would have understood. But I would have happily paid the fee and considered it money well spent. I could have gone on with my marriage with no more worries. Instead, I got a harsh reality check.</p>
              
              <h2>Should You Test Your Partner's Loyalty?</h2>
              <p>I'm not here to tell you what to do, but I can say this was the impetus I needed to validate my worries. I had an inkling something wasn't right, and the loyalty test proved I was right. If you're in the same position, I recommend it. Definitely weigh the pros and cons for yourself, though. Decide whether you can handle the results, whatever they may be.</p>
              
              <div className={css.testerProfile}>
                <img 
                  src={ariProfileImage} 
                  alt="Ari from Brooklyn, loyalty tester" 
                  className={css.gridImage} 
                  loading="lazy"
                  decoding="async"
                  fetchpriority="low"
                />
                <div className={css.testerInfo}>
                  <h3 className={css.testerName}>Meet Ari, My Loyalty Tester</h3>
                  <p>If you're considering a loyalty test, I worked with <a href={testerProfileLink} className={css.testerLink}>Ari from Brooklyn</a>, who was super responsive and sweet. She specializes in Instagram DM loyalty tests and she provided me with clear screenshots of all the screenshots I needed.</p>
                  <p className={css.testerDetails}>
                    <span className={css.testerDetail}>29 • Brooklyn, NY</span>
                    <span className={css.testerDetail}>Part-Time Tester</span>
                    <span className={css.testerDetail}>Specializes in Instagram DM loyalty tests</span>
                  </p>
                </div>
              </div>
              
              {/* FAQ Section with Schema Markup */}
              <div className={css.faqSection}>
                <h2>Frequently Asked Questions About Loyalty Testing</h2>
                
                {faqData.map((faq, index) => (
                  <div key={index} className={css.faqItem}>
                    <h3 className={css.faqQuestion}>{faq.question}</h3>
                    <p className={css.faqAnswer}>{faq.answer}</p>
                  </div>
                ))}
              </div>
              
              {/* Related Articles Section */}
              <div className={css.relatedArticles}>
                <h2>Related Articles</h2>
                
                <div className={css.relatedArticlesGrid}>
                  {relatedArticles.map(article => (
                    <a 
                      key={article.id} 
                      href={article.link}
                      className={css.relatedArticleCard}
                    >
                      <img 
                        src={article.image} 
                        alt={article.title} 
                        className={css.relatedArticleImage} 
                        loading="lazy"
                        width="300"
                        height="180"
                      />
                      <div className={css.relatedArticleContent}>
                        <h3 className={css.relatedArticleTitle}>{article.title}</h3>
                        <p className={css.relatedArticleDescription}>{article.description}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
              
              {/* Join Community Button */}
              <div className={css.joinCommunitySection}>
                <NamedLink name="SearchPage" className={css.joinButton}>
                  Join the Community
                </NamedLink>
              </div>
            </div>
          </div>
        </LayoutWrapperMain>
        
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

export default WhatILearnedFromLoyaltyTestPage; 